import {Dispatch, memo, SetStateAction} from 'react';

import GppGoodIcon from '@mui/icons-material/GppGood';
import {Checkbox, FormControlLabel, Typography} from '@mui/material';
import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {AllowedCountryCodesSignUp} from '@/app/auth/constants/AllowedPhoneNumbers';
import {
  AppButton,
  ButtonContainer,
  PhoneInputTextField,
} from '@/app/shared-components';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import {COLORS} from '@/styles/colors';

import {CHECKBOX_SX, FORM_CONTROL_LABEL_SX} from './constants';
import {useActions} from './useActions';

interface SignUpStep2FormFragmentProps {
  phone: string;
  email: string;
  isCheckboxChecked: boolean;
  isMarketingChecked: boolean;
  isDisabledSignUpStep2: boolean;
  isLoadingAmplifyAuth: boolean;
  isCountryCodeVisible: boolean;
  isMobile: boolean;
  isPhonePrefilledFromInvitation: boolean;
  coParentInviterName?: string;
  setPhone: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setEmailError: Dispatch<SetStateAction<boolean>>;
  setIsCheckboxChecked: Dispatch<SetStateAction<boolean>>;
  handleToggleMarketingChecked: (checked: boolean) => void;
  handleOpenTermsAndConditions: () => void;
  handleOpenPrivacyPolicy: () => void;
  handleOnClickSignUpStep2: () => void;
}

export const SignUpStep2FormFragment = memo(
  ({
    phone,
    email,
    isCheckboxChecked,
    isMarketingChecked,
    isDisabledSignUpStep2,
    isLoadingAmplifyAuth,
    isCountryCodeVisible,
    isMobile,
    isPhonePrefilledFromInvitation,
    coParentInviterName,
    setPhone,
    setEmail,
    setEmailError,
    setIsCheckboxChecked,
    handleToggleMarketingChecked,
    handleOpenTermsAndConditions,
    handleOpenPrivacyPolicy,
    handleOnClickSignUpStep2,
  }: SignUpStep2FormFragmentProps) => {
    const {t} = useTranslation();

    const {
      isMarketingExpanded,
      handleToggleMarketingExpanded,
      handleMarketingChange,
      handleCheckboxChange,
    } = useActions({setIsCheckboxChecked, handleToggleMarketingChecked});

    return (
      <Box className="signup-form-container">
        <form className="signup-form">
          <PhoneInputTextField
            id="SignUpPage_phoneInput"
            value={phone}
            onChange={setPhone}
            countries={AllowedCountryCodesSignUp}
            showCountryCode={isCountryCodeVisible}
            isDisabled={isPhonePrefilledFromInvitation}
          />

          <InputTextField
            id="SignUpPage_emailInput"
            label={t('sign_up.email')}
            placeholder={t('sign_up.email')}
            value={email}
            setValue={setEmail}
            type={'email'}
            onErrorChange={setEmailError}
            labelBackgroundColor={COLORS.AUTH_BACKGROUND}
          />

          {isPhonePrefilledFromInvitation && coParentInviterName && (
            <Box
              id="SignUpPage_coParentSecurityBanner"
              className="co-parent-security-banner">
              <Box className="co-parent-security-banner__icon">
                <GppGoodIcon style={{color: COLORS.ON_SURFACE_VARIANT}} />
              </Box>
              <Box className="co-parent-security-banner__text-container">
                <Typography className="co-parent-security-banner__text">
                  {t('sign_up.co_parent_security_banner_prefix')}{' '}
                  <Box component="span">{coParentInviterName}</Box>{' '}
                  {t('sign_up.co_parent_security_banner_suffix')}
                </Typography>
                <Typography className="co-parent-security-banner__text">
                  {t('sign_up.co_parent_security_banner_resend')}
                </Typography>
              </Box>
            </Box>
          )}

          <FormControlLabel
            className="custom-checkbox-label"
            control={
              <Checkbox
                id="SignUpPage_esignatureCheckbox"
                checked={isCheckboxChecked}
                onChange={handleCheckboxChange}
                inputProps={{
                  'aria-label': 'E-Signature Agreement',
                }}
                sx={CHECKBOX_SX}
              />
            }
            sx={FORM_CONTROL_LABEL_SX}
            label={
              <span className="check-box-label">
                {t('sign_up.read_and_agree')}{' '}
                <span
                  className="check-box-link"
                  onClick={handleOpenTermsAndConditions}>
                  {t('sign_up.terms_and_conditions')}
                </span>{' '}
                {t('sign_up.and')}{' '}
                <span
                  className="check-box-link"
                  onClick={handleOpenPrivacyPolicy}>
                  {t('sign_up.privacy_policy')}
                </span>
              </span>
            }
          />

          <FormControlLabel
            className="custom-checkbox-label marketing-checkbox-label"
            control={
              <Checkbox
                id="SignUpPage_marketingCheckbox"
                checked={isMarketingChecked}
                onChange={handleMarketingChange}
                inputProps={{
                  'aria-label': 'Marketing communications consent',
                }}
                sx={CHECKBOX_SX}
              />
            }
            sx={FORM_CONTROL_LABEL_SX}
            label={
              <span className="check-box-label">
                {t('sign_up.marketing_consent')}
                {isMarketingExpanded ? (
                  <>
                    {t('sign_up.marketing_consent_more')}{' '}
                    <span
                      id="SignUpPage_marketingLessLink"
                      className="check-box-link"
                      onClick={handleToggleMarketingExpanded}>
                      {t('sign_up.less')}
                    </span>
                  </>
                ) : (
                  <>
                    {'… '}
                    <span
                      id="SignUpPage_marketingMoreLink"
                      className="check-box-link"
                      onClick={handleToggleMarketingExpanded}>
                      {t('sign_up.more')}
                    </span>
                  </>
                )}
              </span>
            }
          />
        </form>
        <ButtonContainer sticky>
          {isMobile && (
            <p className="sms-disclaimer">{t('sign_up.sms_disclaimer')}</p>
          )}
          <AppButton
            id="SignUpPage_continueStep2Button"
            label={t('sign_up.continue')}
            onClick={handleOnClickSignUpStep2}
            disabled={!!isDisabledSignUpStep2 || isLoadingAmplifyAuth}
            isLoading={isLoadingAmplifyAuth}
          />
        </ButtonContainer>
        {!isMobile && (
          <div className="signup-disclaimers">
            <p>{t('sign_up.sms_disclaimer')}</p>
            <p>{t('authentication.footer.based_on')}</p>
          </div>
        )}
      </Box>
    );
  },
);
